import { TickerSymbolStatus } from '../features/ticker-symbol/ticker-symbol.domain';
import { Quote } from './quote.domain';
import { SecuritySymbol, SymbolType } from './symbol.domain';
import { RecentQuotesResponse } from './recent-quotes.domain';
import { NewsReport } from './market-info/market-info.domain';

/**
 * A Closeable is a callback into a source component that can be closed.
 * Implementation can be a lambda capturing the 'this' source context.
 */
export interface Closeable {
  close(): void;
}

/**
 * RECENT_QUOTES represents one tab, WATCHLISTS represents many tabs.
 */
export enum WatchlistTab {
  RECENT_QUOTES = 'RECENT_QUOTES',
  WATCHLISTS = 'WATCHLISTS'
}

/**
 * Three sub-tabs within a Quote or Watchlist tab.
 */
export type WatchlistSubTab = 'QUOTE' | 'FUNDAMENTALS' | 'TRACKER';

// ---------- backend response types ---------- //

type WatchlistQuoteFields =
  'lastPrice' |
  'latestPriceChange' |
  'latestPercentChange' |
  'ask' |
  'bid' |
  'volume' |
  'yearLow' |
  'yearHigh' |
  'bidSize' |
  'askSize' |
  'description' |
  'desc' |
  'rrspEligibility' |
  'lowPrice' |
  'highPrice' |
  'exchangeAgreementDeclined' |
  'quoteError' |
  'priceChange' |
  'percentChange' |
  'securityHalted' |
  'expired' |
  'quoteValid' |
  'symbolError' |
  'purchasePriceChange' |
  'purchasePriceChangePercentage' |
  'logoLight';

export interface WatchlistTracker {
  quantity?: number;
  unitPrice?: number;
}

export type WatchlistQuote = Pick<Quote, WatchlistQuoteFields> & WatchlistTracker;
export type WatchlistItemResponseSymbol = Pick<SecuritySymbol,
'symbolType' |
'displaySymbol' |
'marketType' |
'tickerSymbol' |
'market' |
'optionExpired' |
'call' |
'valid' |
'symbolError' |
'id'>;

export type WatchlistItemResponse = WatchlistQuote & WatchlistItemResponseSymbol & {
  fundamentalData?: FundamentalData;
  halted?: boolean;
  purchasePriceChange: number;
  purchasePriceChangePercentage: number;
  marketValue: number;
  bookValue: number;
  tradeableSecurityType?: SymbolType;
  gainLoss: number;
  news?: NewsReport;
};

export type WatchlistResponseHoldings = { [key in SymbolType ]: Array<WatchlistItemResponse> };

export type CopySource = Watchlist | WatchlistItem | WatchlistItemResponse | RecentQuotesResponse | WatchlistResponse;

// WatchlistResponse is also used as a request object when saving watchlist.
export interface WatchlistResponse {

  number: number;
  name: string;
  timestamp: Date;
  holdings: WatchlistResponseHoldings;
  totalBookValue: number;
  totalGainLoss: number;
  totalGainLossPercentage: number;
  totalMarketValue: number;
  totals: WatchlistEntriesTotals;
}

// ---------- converted types ----------------- //

export interface BaseAsset {
  symbol: SecuritySymbol;
  quote: WatchlistQuote;
  fundamentalData?: FundamentalData;
  tracker?: Tracker;
  tickerSymbolStatus?: TickerSymbolStatus;
}

export type RecentQuotesItem  = BaseAsset;

export interface WatchlistItem extends BaseAsset {
  editableFields?: string;

  // the following two fields represent top movers' terse quote symbol being added to a watchlist
  tickerSymbol?: string;
  market?: string;
}

/**
 * @see AssetType
 */
export class AssetGroups<T extends BaseAsset> {
  constructor(public ALL_SECURITY_TYPES: Array<T> = [],
              public INDEX: Array<T> = [],
              public MUTUALFUND: Array<T> = [],
              public OPTION: Array<T> = [],
              public STOCK: Array<T> = []) {}

  /**
   * @param item is a WatchlistItem | RecentQuotesItem | WatchlistItemResponse instance.
   */
  public add(item: T): void {
    this[item.symbol.symbolType].push(item);
    this.ALL_SECURITY_TYPES.push(item);
  }
}

// shared by Watchlist and RecentQuotes
export interface SymbolGroup<T extends BaseAsset> {
  name: string;
  numberOfRemaining: number;
  timestamp: Date;
  assetGroups: AssetGroups<T>;
  hasFundamentals: boolean;
}

export type RecentQuotes = SymbolGroup<RecentQuotesItem>;

export interface Watchlist extends SymbolGroup<WatchlistItem> {

  number: number;
  hasQuote: boolean; // unlike other fields, hasQuote doesn't come from backend: it's set to true when a detailed watchlist was requested
  phantom: boolean;
  totalBookValue: number;
  totalGainLoss: number;
  totalGainLossPercentage: number;
  totalMarketValue: number;
  totals?: WatchlistEntriesTotals;
}

export interface WatchlistEntriesTotals {
  STOCK?: WatchlistEntriesTotal;
  MUTUALFUND?: WatchlistEntriesTotal;
  OPTION?: WatchlistEntriesTotal;
}

export interface WatchlistEntriesTotal {
  bookValue: number;
  gainLoss: number;
  gainLossPercentage: number;
  marketValue: number;
}

export interface FundamentalData {
  companyName: string;
  dividendAmountLast: number;
  dividendYield: number;
  dividendExDate: Date;
  nextEarningsDate: Date;
  estimatedEarnings: number;
  marketCap: number;
  peRatio: number;
}

export interface Tracker {
  unitPrice: number;
  quantity: number;
  purchasePriceChange: number;
  purchasePriceChangePercentage: number;
  marketValue: number;
  bookValue: number;
  gainLoss: number;
  gainLossPercentage: number;
}

// --- table column types --- //

export enum WatchlistColumn {
  QUANTITY = 'quantity',
  LAST_PRICE = 'lastPrice',
  AVERAGE_COST = 'avgCost',
  BOOK_COST = 'bookCost',
  CHANGE = 'change',
  PRICE_CHANGE_PERCENTAGE = 'priceChangeProcentage', // Is it the same as CHANGE?
  GAIN_LOSS_UNREALIZED = 'gainLossUnrealized',
  BID_LOTS = 'bidLots',
  ASK_LOTS = 'askTols',
  VOLUME = 'volume',
  DAY_RANGE_HIGH_LOW = 'dayRangeHighLow',
  FIFTY_TWO_WEEK_RANGE_HIGH_LOW = '52weekRangeHighLow',
  MARKET_CAP = 'marketCap',
  P_E_RATIO = 'peRatio',
  ESTIMATED_EPS = 'estimatedEps',
  NEXT_EARNINGS = 'NextEarnings',
  PREV_DIV_AMOUNT = 'PrefDivAmount',
  DIV_YIELD = 'divYield',
  EX_DIV_YIELD = 'exDivYield'
}

/**
 * @see SymbolType at symbol.domain.ts
 */
export enum AssetType {
  ALL_SECURITY_TYPES = 'ALL_SECURITY_TYPES', // aka 'FLAT'
  INDEX = 'INDEX',           // aka 'INDICES'
  MUTUALFUND = 'MUTUALFUND', // aka 'MUTUAL_FUNDS'
  OPTION = 'OPTION',         // aka 'OPTIONS'
  STOCK = 'STOCK'            // aka 'STOCKS_AND_ETFS'
}

export type WatchlistColumns = {
  [key in WatchlistSubTab]: Array<WatchlistColumn>;
};

export type WatchlistTableColumnsByAssetsGroups = {
  [assetGroupKey in AssetType]: {[watchlistSubTabKey in WatchlistSubTab]: Array<number>};
};

export type WatchlistTableColumns = {
  [key in AssetType]: Array<number>;
};

export interface AddWatchlistSymbolParams {
  symbol: string;
  market: string;
  symbolType: string;
}
